import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, errorCode, errorDetails, errorMessage } from '../lib/api';
import type { Sitting } from '../lib/sitting';

/**
 * The state of one sitting: the paper, the answers, and the clock.
 *
 * Two rules shape everything here. Answers are written through to the server as
 * they are given, so a lost connection costs seconds rather than the paper. And
 * the countdown is a *display* of the server's deadline, corrected for the
 * difference between the two clocks — the server refuses late writes regardless,
 * so the only thing a wrong client clock can do is mislead the candidate, and
 * this is what stops it doing even that.
 */

const AUTOSAVE_DEBOUNCE_MS = 600;
const HEARTBEAT_MS = 30_000;

/**
 * The time a candidate has after a Listening recording stops. The server's
 * LISTENING_TAIL_MS is the authority; this is only what the screen shows for
 * the moment before the server has answered.
 */
const LISTENING_TAIL_MS = 110_000;

export interface AnswerState {
  value: string;
  flagged: boolean;
}

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export function useSitting(sittingId: string) {
  const [sitting, setSitting] = useState<Sitting | null>(null);
  const [answers, setAnswers] = useState<Map<number, AnswerState>>(new Map());
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sebRequired, setSebRequired] = useState<'NO_HEADER' | 'NO_MATCH' | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [msRemaining, setMsRemaining] = useState<number>(0);
  const [submitted, setSubmitted] = useState(false);

  /** serverNow − clientNow at load. Added to Date.now() to read the exam clock. */
  const driftRef = useRef(0);
  const deadlineRef = useRef<number>(0);
  const pending = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const submittingRef = useRef(false);

  /**
   * Answers whose write did not land.
   *
   * A save is attempted once, on a debounce. If that one attempt fails — a
   * blink of connection, a token being refreshed underneath it — the answer
   * lived only in this browser: the debounce had already fired, so nothing was
   * scheduled to try again, and only typing in that same box would have saved
   * it. A candidate who answered a question and moved on lost it, and found
   * out when the result came back.
   *
   * They are retried on every heartbeat and again before the paper is handed
   * in, which is the promise this hook is supposed to keep.
   */
  const unsaved = useRef(new Set<number>());

  /**
   * The answers, held in a ref as well as in state.
   *
   * Autosave must never read them through a closure: a handler captured on an
   * earlier render sees an earlier map, and a flush driven by such a closure
   * writes stale values — or, worse, finds nothing to write and silently drops
   * the answer. The ref is the source of truth for saving; the state exists to
   * render.
   */
  const answersRef = useRef<Map<number, AnswerState>>(new Map());

  const applySitting = useCallback((data: Sitting) => {
    setSitting(data);
    const loaded = new Map(
      data.answers.map(
        (a) => [a.questionNumber, { value: a.value, flagged: a.flaggedForReview }] as const,
      ),
    );
    answersRef.current = new Map(loaded);
    setAnswers(new Map(loaded));
    driftRef.current = new Date(data.session.serverNow).getTime() - Date.now();
    deadlineRef.current = new Date(data.session.deadlineAt).getTime();
    setMsRemaining(Math.max(0, deadlineRef.current - (Date.now() + driftRef.current)));
    setSubmitted(data.session.state !== 'IN_PROGRESS');
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .get<Sitting>(`/my/sittings/${sittingId}`)
      .then(({ data }) => !cancelled && applySitting(data))
      .catch((err) => {
        if (cancelled) return;
        // Carried through as a code, not only a sentence: a paper refused for
        // being outside Safe Exam Browser gets its own screen, and a candidate
        // resuming one in the wrong browser needs that screen as much as the
        // one who never started.
        if (errorCode(err) === 'SEB_REQUIRED') {
          setSebRequired(
            errorDetails<{ reason: 'NO_HEADER' | 'NO_MATCH' }>(err)?.reason ?? 'NO_HEADER',
          );
        }
        setLoadError(errorMessage(err, 'Could not open this paper'));
      });
    return () => {
      cancelled = true;
    };
  }, [sittingId, applySitting]);

  const submit = useCallback(
    async (auto: boolean) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      try {
        // Everything still on the debounce, and everything an earlier attempt
        // failed to write, goes before the paper ends — or the last answer
        // typed, and any the network ate, would never reach the server.
        const outstanding = new Map<number, ReturnType<typeof setTimeout> | null>([
          ...[...pending.current.entries()].map(
            ([n, t]) => [n, t] as [number, ReturnType<typeof setTimeout>],
          ),
          ...[...unsaved.current].map((n) => [n, null] as [number, null]),
        ]);
        for (const [number, timer] of outstanding.entries()) {
          if (timer) clearTimeout(timer);
          const answer = answersRef.current.get(number);
          if (answer) {
            await api
              .put(`/my/sittings/${sittingId}/answers/${number}`, {
                value: answer.value,
                flaggedForReview: answer.flagged,
              })
              .catch(() => undefined);
          }
        }
        pending.current.clear();
        unsaved.current.clear();
        await api.post(`/my/sittings/${sittingId}/submit`, { auto });
        setSubmitted(true);
      } catch {
        // A failed submit still ends the paper for the candidate: the server's
        // sweep closes an overdue sitting either way, so nothing is lost.
        setSubmitted(true);
      }
    },
    [sittingId],
  );

  const flush = useCallback(
    async (number: number, next: AnswerState) => {
      setSaveState('saving');
      try {
        await api.put(`/my/sittings/${sittingId}/answers/${number}`, {
          value: next.value,
          flaggedForReview: next.flagged,
        });
        unsaved.current.delete(number);
        setSaveState('saved');
      } catch (err) {
        setSaveState('error');
        // A refused write after the deadline is not a fault to retry; the paper
        // is over and the candidate is about to be told so.
        if (errorMessage(err).includes('Time is up')) {
          unsaved.current.delete(number);
          setSubmitted(true);
          return;
        }
        unsaved.current.add(number);
      }
    },
    [sittingId],
  );

  // The visible countdown. It ticks locally so it stays smooth, but every value
  // it shows is derived from the server's deadline.
  useEffect(() => {
    if (!sitting || submitted) return;
    const tick = () => {
      const left = Math.max(0, deadlineRef.current - (Date.now() + driftRef.current));
      setMsRemaining(left);
      if (left <= 0) void submit(true);
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [sitting, submitted, submit]);

  // Heartbeat: re-anchors the clock against the server and notices a sitting
  // that was closed elsewhere (the overdue sweep, or another tab submitting).
  useEffect(() => {
    if (!sitting || submitted) return;
    const id = setInterval(() => {
      api
        .post<{ msRemaining: number; state: string; serverNow: string }>(
          `/my/sittings/${sittingId}/heartbeat`,
        )
        .then(({ data }) => {
          driftRef.current = new Date(data.serverNow).getTime() - Date.now();
          if (data.state !== 'IN_PROGRESS') {
            setSubmitted(true);
            return;
          }
          // The connection is back, so anything that failed to save gets
          // another go — every thirty seconds until it lands.
          for (const number of [...unsaved.current]) {
            const answer = answersRef.current.get(number);
            if (answer) void flush(number, answer);
          }
        })
        .catch(() => undefined);
    }, HEARTBEAT_MS);
    return () => clearInterval(id);
  }, [sitting, submitted, sittingId, flush]);

  /**
   * Tells the server the recording has stopped, and takes the clock it sends
   * back.
   *
   * The 1m 50s to check answers starts from the moment the player runs out of
   * sound, and only the player knows that moment. The reply is adopted at once,
   * rather than on the next heartbeat thirty seconds away: until it arrives the
   * candidate is looking at the backstop, which is minutes too long.
   *
   * Tried again if it fails. A lost report leaves the backstop standing, and
   * that is a sitting running several minutes past its end.
   */
  const recordingEnded = useCallback(async () => {
    if (!sittingId) return;
    /*
     * The tail shown at once, before the server has answered.
     *
     * Without this the first frame after the recording stopped read the
     * backstop — "6 minutes, 49 seconds remaining" — for as long as the
     * request took, and then jumped to 1m 50s. It is exactly what the server
     * is about to set, and the server's own answer replaces it a moment later.
     */
    deadlineRef.current = Date.now() + driftRef.current + LISTENING_TAIL_MS;
    setMsRemaining(LISTENING_TAIL_MS);
    for (let attempt = 0; attempt < 5; attempt += 1) {
      try {
        const { data } = await api.post<{
          deadlineAt: string;
          msRemaining: number;
          serverNow: string;
          state: string;
        }>(`/my/sittings/${sittingId}/recording-ended`);
        driftRef.current = new Date(data.serverNow).getTime() - Date.now();
        deadlineRef.current = new Date(data.deadlineAt).getTime();
        setMsRemaining(Math.max(0, deadlineRef.current - (Date.now() + driftRef.current)));
        if (data.state !== 'IN_PROGRESS') setSubmitted(true);
        return;
      } catch {
        await new Promise((resolve) => setTimeout(resolve, 1_000 * (attempt + 1)));
      }
    }
  }, [sittingId]);

  const setAnswer = useCallback(
    (number: number, patch: Partial<AnswerState>) => {
      const merged: AnswerState = {
        value: '',
        flagged: false,
        ...answersRef.current.get(number),
        ...patch,
      };
      const next = new Map(answersRef.current);
      next.set(number, merged);
      answersRef.current = next;
      setAnswers(next);

      // Scheduling happens here rather than inside the state updater: an
      // updater may be invoked more than once for a single change, which would
      // schedule the same write repeatedly.
      const existing = pending.current.get(number);
      if (existing) clearTimeout(existing);
      pending.current.set(
        number,
        setTimeout(() => {
          pending.current.delete(number);
          void flush(number, merged);
        }, AUTOSAVE_DEBOUNCE_MS),
      );
    },
    [flush],
  );

  const flushPending = useCallback(() => {
    for (const [number, timer] of pending.current.entries()) {
      clearTimeout(timer);
      const answer = answersRef.current.get(number);
      if (answer) void flush(number, answer);
    }
    pending.current.clear();

    // Anything an earlier attempt failed to write goes with them.
    for (const number of [...unsaved.current]) {
      const answer = answersRef.current.get(number);
      if (answer) void flush(number, answer);
    }
  }, [flush]);

  /**
   * A closing tab, or leaving the page, must not take the last keystroke with
   * it. Registered once: an earlier version depended on `answers`, so every
   * keystroke re-ran the effect, and its cleanup cancelled the write that had
   * just been scheduled — answers were never saved at all.
   */
  useEffect(() => {
    window.addEventListener('pagehide', flushPending);
    return () => {
      window.removeEventListener('pagehide', flushPending);
      flushPending();
    };
  }, [flushPending]);

  const answeredCount = useMemo(
    () => [...answers.values()].filter((a) => a.value.trim() !== '').length,
    [answers],
  );

  return {
    sitting,
    loadError,
    sebRequired,
    answers,
    setAnswer,
    saveState,
    msRemaining,
    submitted,
    submit,
    answeredCount,
    recordingEnded,
  };
}
